<template>
  <div>
    <PTransparentLoader :loading="isLoadingScopes">
      <!-- Empty state: no access -->
      <template v-if="!isLoadingScopes && hasNoCMs">
        <CCard>
          <CCardBody class="text-center py-5">
            <h4 v-translate translate-context="payout.empty">
              No access
            </h4>
            <p class="text-muted" v-translate translate-context="payout.empty">
              You do not have access to any contract manager for managing payments.
            </p>
          </CCardBody>
        </CCard>
      </template>

      <!-- Main content -->
      <template v-else-if="!isLoadingScopes && selectedCmId">
        <!-- Header -->
        <div class="mb-1">
          <div class="d-flex align-items-center justify-content-between ">
            <div>
              <h1 class="main-header mb-0 mr-3" v-translate translate-context="payout">
                Payments
              </h1>
              <p v-translate translate translate-context="payout">
                Check accrued amounts and status of invoices
              </p>
            </div>

            <ContractManagerSelector
              v-if="hasMultipleCMs"
              :contract-managers="contractManagers"
              :selected-id="selectedCmId"
              @select="onSelectContractManager"
            />
          </div>
        </div>

        <!-- Certification Banner -->
        <CertificationBanner
          :certification="certification"
          @open-certification="openCertificationModal"
        />

        <!-- Current Month Card -->
        <CurrentMonthCard
          :statistics="statistics"
          :is-loading="isLoadingStatistics"
          @download-statistics="onDownloadStatistics"
        />

        <!-- Pagamenti in lavorazione -->
        <CCard v-if="payoutsInProgress.results.length > 0" class="mb-4">
          <CCardHeader class="border-0 px-4 pb-1 pt-4">
            <div class="d-flex align-items-center font-weight-medium">
              <CIcon name="cipCreditCard02" size="custom-size" :width="18" class="mr-2"/>
              <translate translate-context="accounting.refund">Payments in progress</translate>
            </div>
          </CCardHeader>
          <CCardBody>
            <PayoutsInProgressTable
              :payouts="payoutsInProgress.results"
              :expanded-data="expandedPayoutsData"
              @expand="onExpandPayoutRow"
              @upload-invoice="onUploadInvoice"
              @download-payout-excel="onDownloadPayoutExcel"
            />
          </CCardBody>
        </CCard>

        <!-- Storico pagamenti -->
        <div class="mb-4">
          <CCard>
            <CCardHeader class="border-0 px-4 pb-1 pt-4">
              <div class="d-flex align-items-center font-weight-medium">
                <CIcon name="cipClockCheck" size="custom-size" :width="18" class="mr-2"/>
                <translate translate-context="accounting.refund">Payments history</translate>
              </div>
            </CCardHeader>
            <CCardBody>
              <PTransparentLoader :loading="isLoadingList">
                <template v-if="payoutsHistory.results.length === 0 && !isLoadingList">
                  <div class="text-center py-5">
                    <CIcon name="cil-wallet" size="xl" class="text-muted mb-3"/>
                    <h5 v-translate translate-context="payout.history">
                      No payments
                    </h5>
                    <p class="text-muted" v-translate translate-context="payout.history">
                      There are no payments yet.
                    </p>
                  </div>
                </template>

                <template v-else>
                  <PayoutsHistoryTable
                    :payouts="payoutsHistory.results"
                    :expanded-data="expandedPayoutsData"
                    @expand="onExpandPayoutRow"
                    @download-payout-excel="onDownloadPayoutExcel"
                  />
                </template>
              </PTransparentLoader>
            </CCardBody>

            <!-- Pagination -->
            <CCardFooter
              v-if="payoutsHistory.pagination && payoutsHistory.pagination.total > historyPagination.page_size">
              <CRow class="align-items-center">
                <CCol sm="6">
                  <span class="text-muted">
                    <translate
                      translate-context="payout.pagination"
                      :translate-params="{
                        from: ((historyPagination.page - 1) * historyPagination.page_size) + 1,
                        to: Math.min(historyPagination.page * historyPagination.page_size, payoutsHistory.pagination.total),
                        total: payoutsHistory.pagination.total
                      }"
                    >
                      Mostrando %{from} - %{to} di %{total}
                    </translate>
                  </span>
                </CCol>
                <CCol sm="6">
                  <CPagination
                    :activePage.sync="historyPagination.page"
                    :pages="historyTotalPages"
                    align="end"
                    @update:activePage="onHistoryPageChange"
                  />
                </CCol>
              </CRow>
            </CCardFooter>
          </CCard>
        </div>

        <!-- Invoice Upload Modal -->
        <InvoiceUploadModal
          ref="invoiceModal"
          :is-uploading="isUploadingInvoice"
          @submit="onSubmitInvoice"
        />

      </template>
    </PTransparentLoader>
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import ContractManagerSelector from './components/ContractManagerSelector.vue'
import CertificationBanner from './components/CertificationBanner.vue'
import CurrentMonthCard from './components/CurrentMonthCard.vue'
import eventBus from '@/domain/core/constant/eventBus'
import PayoutsInProgressTable from './components/PayoutsInProgressTable.vue'
import PayoutsHistoryTable from './components/PayoutsHistoryTable.vue'
import InvoiceUploadModal from './components/InvoiceUploadModal.vue'

export default {
  name: 'PayoutDashboard',
  components: {
    PTransparentLoader,
    ContractManagerSelector,
    CertificationBanner,
    CurrentMonthCard,
    PayoutsInProgressTable,
    PayoutsHistoryTable,
    InvoiceUploadModal
  },
  data() {
    return {
      historyPagination: {
        page: 1,
        page_size: 20
      },
      expandedPayoutsData: {}
    }
  },
  computed: {
    ...mapGetters('payout', [
      'contractManagers',
      'hasMultipleCMs',
      'hasNoCMs',
      'selectedCmId',
      'selectedCm',
      'certification',
      'statistics',
      'payoutsInProgress',
      'payoutsHistory',
      'isLoadingScopes',
      'isLoadingList',
      'isLoadingStatistics',
      'isUploadingInvoice'
    ]),
    historyTotalPages() {
      if (!this.payoutsHistory.pagination) return 0
      return Math.ceil(this.payoutsHistory.pagination.total / this.historyPagination.page_size)
    }
  },
  methods: {
    ...mapActions('payout', [
      'fetchScopes',
      'selectContractManager',
      'fetchPayoutsHistory',
      'fetchPayoutDetail',
      'downloadStatistics',
      'downloadPayout',
      'uploadInvoice'
    ]),

    async onSelectContractManager(cmId) {
      this.historyPagination.page = 1
      this.expandedPayoutsData = {}
      await this.selectContractManager(cmId)
    },

    openCertificationModal() {
      this.emitEvent(eventBus.certification.open, this.selectedCmId)
    },

    async onCertificationUpdated() {
      await this.selectContractManager(this.selectedCmId)
    },

    onUploadInvoice(payout) {
      if (this.$refs.invoiceModal) {
        this.$refs.invoiceModal.open(payout)
      }
    },

    async onSubmitInvoice({payoutId, invoice}) {
      try {
        await this.uploadInvoice({payoutId, invoice})
        this.notifySuccess(this.$pgettext('payout', 'Fattura caricata con successo'))
        if (this.$refs.invoiceModal) {
          this.$refs.invoiceModal.close()
        }
      } catch (error) {
        const message = error.response?.data?.detail ||
          this.$pgettext('payout', 'Errore nel caricamento della fattura')
        this.notifyError(message)
      }
    },

    async onDownloadStatistics() {
      try {
        await this.downloadStatistics()
      } catch (error) {
        this.notifyError(this.$pgettext('payout', 'Errore nel download'))
      }
    },

    async onDownloadPayoutExcel(payout) {
      try {
        await this.downloadPayout(payout.id)
      } catch (error) {
        this.notifyError(this.$pgettext('payout', 'Errore nel download'))
      }
    },

    async onExpandPayoutRow(payoutId) {
      if (this.expandedPayoutsData[payoutId]) return
      try {
        const detail = await this.fetchPayoutDetail(payoutId)
        if (detail.payout_pica_services && !detail.pica_services) {
          detail.pica_services = detail.payout_pica_services
        }
        this.$set(this.expandedPayoutsData, payoutId, detail)
      } catch (error) {
        this.notifyError(this.$pgettext('payout', 'Errore nel caricamento dei dettagli'))
      }
    },

    async onHistoryPageChange(page) {
      this.historyPagination.page = page
      this.expandedPayoutsData = {}
      await this.fetchPayoutsHistory({
        page: this.historyPagination.page,
        page_size: this.historyPagination.page_size
      })
    }
  },
  mounted() {
    this.registerEvent(eventBus.certification.submitted, this.onCertificationUpdated)
    this.fetchScopes()
  },
  beforeDestroy() {
    this.unregisterEvent(eventBus.certification.submitted, this.onCertificationUpdated)
  }
}
</script>
